// --- proxy created once at module level for reactive reads ---

import { create } from '@/shared/utils/zustandVue'

import { t } from '../../i18n/i18n'
import { ErrorService } from '../../shared/service/errorService'
import { weightService } from '../../shared/service/utils.service'
import { SnackbarService } from '../../shared/snackbar/snackbar.service'
import { type ProgEvent } from '../../shared/types/progEvent.type'
import { type Scene } from '../../shared/types/scene.type'
import { ScenesService } from './scenes.service'

class ScenesState {
  namespaced = true
  scenes: Record<string, Scene> = {}
  deletingSceneId: string | null = null
  editingSceneId: string | null = null
  loading = false
  showModal = false
  sceneToMoveId: string | null = null
  deletingSceneEvents: ProgEvent[] = []
  deletingSceneMarkers: { _id: string; name: string }[] = []
}

export function sortScenes(a: Scene, b: Scene): number {
  if (a.weight === b.weight) {
    if (a.names.fr < b.names.fr) return -1
    if (a.names.fr > b.names.fr) return 1
    return 0
  }
  return b.weight - a.weight
}

type ScenesStore = ScenesState & {
  load: () => void
  unload: () => void
  setScenes: (scenes: Scene[]) => void
  setDeleting: (id: string) => void
  setNotDeleting: () => void
  setAdding: () => void
  setEditing: (id: string) => void
  reset: () => void
  setSceneEvents: (events: ProgEvent[]) => void
  setSceneMarkers: (markers: { _id: string; name: string }[]) => void
  list: () => Promise<void>
  deleteScene: () => Promise<void>
  addScene: (scene: Scene) => Promise<void>
  move: (payload: { sceneToMove: Scene; up_down: number }) => Promise<void>
  reorder: (scenesList: Scene[]) => Promise<void>
  setDeletionMode: (sceneId: string) => Promise<void>
}

/**
 * Scenes store — idiomatic zustand-vue.
 *
 * State + actions in a single `create` call. Mutations from the old Vuex
 * facade are folded into actions that call `set`. The `get` parameter is
 * used to read current state inside actions.
 */
export const useScenesStore = create<ScenesStore>((set, get) => ({
  ...new ScenesState(),

  // --- mutations (now actions) ---
  load: () => {
    set({ loading: true })
  },
  unload: () => {
    set({ loading: false })
  },
  setScenes: (scenes) => {
    set({
      scenes: scenes.reduce<Record<string, Scene>>((acc, scene) => {
        acc[scene._id] = scene
        return acc
      }, {}),
    })
  },
  setDeleting: (id) => {
    set({ deletingSceneId: id })
  },
  setNotDeleting: () => {
    set({ deletingSceneId: null })
  },
  setAdding: () => {
    set({ showModal: true })
  },
  setEditing: (id) => {
    set({ editingSceneId: id, showModal: true })
  },
  reset: () => {
    set({ editingSceneId: null, showModal: false })
  },
  setSceneEvents: (events) => {
    set({ deletingSceneEvents: [...events] })
  },
  setSceneMarkers: (markers) => {
    set({ deletingSceneMarkers: [...markers] })
  },

  // --- actions ---
  list: async () => {
    set({ loading: true })
    try {
      const scenes = await ScenesService.getAll()
      get().setScenes(scenes)
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      set({ loading: false })
    }
  },

  deleteScene: async () => {
    set({ loading: true })
    try {
      const { deletingSceneId, scenes } = get()
      if (deletingSceneId == null || deletingSceneId === '') {
        return
      }
      await ScenesService.deleteScene(deletingSceneId)
      SnackbarService.info(
        t('SCENES.SNACK_BAR.DELETE', {
          string: scenes[deletingSceneId].names.fr,
        }),
      )
      await get().list()
    } catch (e) {
      ErrorService.handleError(e)
      set({ loading: false })
    } finally {
      get().setNotDeleting()
    }
  },

  addScene: async (scene) => {
    set({ loading: true })
    try {
      let message = ''
      const { editingSceneId } = get()
      if (editingSceneId != null && editingSceneId !== '') {
        await ScenesService.updateScene(scene)
        message = t('SCENES.SNACK_BAR.EDIT', {
          string: scene.names.fr,
        })
      } else {
        scene.weight = weightService.getMinWeight(get().scenes)
        await ScenesService.addScene(scene)
        message = t('SCENES.SNACK_BAR.ADD', {
          string: scene.names.fr,
        })
      }
      SnackbarService.info(message)
      get().reset()
      await get().list()
    } catch (e) {
      ErrorService.handleError(e)
      set({ loading: false })
    }
  },

  move: async (payload) => {
    set({ loading: true })
    try {
      const { scenes } = get()
      const tab = Object.values(scenes).sort(sortScenes)
      const index = tab.indexOf(scenes[payload.sceneToMove._id])
      const { weight } = tab[index]
      tab[index].weight = tab[index + payload.up_down].weight
      tab[index + payload.up_down].weight = weight
      const sceneToMoveId = tab[index + payload.up_down]._id
      set({ sceneToMoveId, scenes: { ...scenes } })

      await ScenesService.updateScene(get().scenes[payload.sceneToMove._id])
      await ScenesService.updateScene(get().scenes[sceneToMoveId])

      set({ sceneToMoveId: null })
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      set({ loading: false })
    }
  },

  reorder: async (scenesList) => {
    try {
      const { scenes } = get()
      // Display order is weight ascending (index 0 = lowest weight = top).
      // Reassign the existing sorted weight values to the new positions so
      // only the moved range actually changes. No `loading` set — the
      // data-table spinner would unmount the draggable mid-drag.
      const weights = Object.values(scenes)
        .map((s) => s.weight)
        .sort((a, b) => a - b)
      const scenesUpdated = { ...scenes }
      const promises: Promise<unknown>[] = []
      scenesList.reverse().forEach((scene, index) => {
        const newWeight = weights[index]
        if (scenes[scene._id].weight !== newWeight) {
          scenesUpdated[scene._id] = {
            ...scenes[scene._id],
            weight: newWeight,
          }
          promises.push(ScenesService.updateScene(scenesUpdated[scene._id]))
        }
      })
      set({ scenes: scenesUpdated })
      await Promise.all(promises)
    } catch (e) {
      ErrorService.handleError(e)
    }
  },

  setDeletionMode: async (sceneId) => {
    set({ loading: true })
    try {
      const [eventList, markers] = await Promise.all([
        ScenesService.getSceneEvents(sceneId),
        ScenesService.getSceneMarkers(sceneId),
      ])
      get().setSceneEvents(eventList)
      get().setSceneMarkers(markers)
      get().setDeleting(sceneId)
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      set({ loading: false })
    }
  },
}))
